// apps/admin/src/components/atomic-crm/root/CRM.tsx

import { useEffect } from "react";
import { Route } from "react-router";

import {
  type AuthProvider,
  CustomRoutes,
  localStorageStore,
  Resource,
  type DataProvider,
  type CoreAdminProps,
} from "ra-core";

import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";

import { Admin } from "@/components/admin/admin";
import { useIsMobile } from "@/hooks/use-mobile";

import { ForgotPasswordPage } from "@/components/supabase/forgot-password-page";
import { OAuthConsentPage } from "@/components/supabase/oauth-consent-page";
import { SetPasswordPage } from "@/components/supabase/set-password-page";

// BldrOS dashboards + routes
import { BldrOsDashboard } from "@/components/bldros/dashboard/BldrOsDashboard";
import { MobileBldrOsDashboard } from "@/components/bldros/dashboard/MobileBldrOsDashboard";
import { OrgSettingsPage } from "@/components/bldros/org/OrgSettingsPage";
import { PipelineDashboard } from "@/components/bldros/pipeline/PipelineDashboard";
import { SuperAdminDashboard } from "@/components/bldros/admin/SuperAdminDashboard";
import { SubList } from "@/components/bldros/subs/SubList";
import { VendorList } from "@/components/bldros/vendors/VendorList";

// Public pages (no auth)
import { CustomerPortalPage } from "@/components/bldros/portal/CustomerPortalPage";
import { PublicEstimatePage } from "@/components/bldros/estimates/public/PublicEstimatePage";
import { VendorPortalPage } from "@/components/bldros/vendors/VendorPortalPage";

// ✅ These provide list/show/etc so Estimates/Jobs/Invoices pages actually exist
import estimates from "@/components/bldros/estimates";
import jobs from "@/components/bldros/jobs";
import invoices from "@/components/bldros/invoices";

// Atomic CRM resources
import companies from "../companies";
import contacts from "../contacts";
import deals from "../deals";
import sales from "../sales";

// Atomic CRM layout + pages
import { Layout } from "../layout/Layout";
import { MobileLayout } from "../layout/MobileLayout";
import { CompanyShow } from "../companies/CompanyShow";
import { ContactListMobile } from "../contacts/ContactList";
import { ContactShow } from "../contacts/ContactShow";
import { MobileTasksList } from "../tasks/MobileTasksList";

import { ConfirmationRequired } from "../login/ConfirmationRequired";
import { SignupPage } from "../login/SignupPage";
import { StartPage } from "../login/StartPage";
import { ImportPage } from "../misc/ImportPage";
import { SettingsPage } from "../settings/SettingsPage";

// Providers + config
import {
  authProvider as defaultAuthProvider,
  dataProvider as defaultDataProvider,
} from "../providers/supabase";

import type { ConfigurationContextValue } from "./ConfigurationContext";
import { ConfigurationProvider } from "./ConfigurationContext";
import {
  defaultCompanySectors,
  defaultContactGender,
  defaultDarkModeLogo,
  defaultDealCategories,
  defaultDealPipelineStatuses,
  defaultDealStages,
  defaultLightModeLogo,
  defaultNoteStatuses,
  defaultTaskTypes,
  defaultTitle,
} from "./defaultConfiguration";
import { i18nProvider } from "./i18nProvider";

export type CRMProps = {
  dataProvider?: DataProvider;
  authProvider?: AuthProvider;
  disableTelemetry?: boolean;
} & Partial<ConfigurationContextValue>;

export const CRM = ({
  contactGender = defaultContactGender,
  companySectors = defaultCompanySectors,
  dealCategories = defaultDealCategories,
  dealPipelineStatuses = defaultDealPipelineStatuses,
  dealStages = defaultDealStages,
  darkModeLogo = defaultDarkModeLogo,
  lightModeLogo = defaultLightModeLogo,
  noteStatuses = defaultNoteStatuses,
  taskTypes = defaultTaskTypes,
  title = defaultTitle,
  dataProvider = defaultDataProvider,
  authProvider = defaultAuthProvider,
  googleWorkplaceDomain = import.meta.env.VITE_GOOGLE_WORKPLACE_DOMAIN,
  disableTelemetry,
  ...rest
}: CRMProps) => {
  useEffect(() => {
    // Atomic CRM telemetry pixel (kept for parity, can be disabled via prop)
    if (
      disableTelemetry ||
      process.env.NODE_ENV !== "production" ||
      typeof window === "undefined" ||
      typeof window.location === "undefined" ||
      typeof Image === "undefined"
    ) {
      return;
    }
    const img = new Image();
    img.src = `https://atomic-crm-telemetry.marmelab.com/atomic-crm-telemetry?domain=${window.location.hostname}`;
  }, [disableTelemetry]);

  const isMobile = useIsMobile();
  const ResponsiveAdmin = isMobile ? MobileAdmin : DesktopAdmin;

  return (
    <ConfigurationProvider
      contactGender={contactGender}
      companySectors={companySectors}
      dealCategories={dealCategories}
      dealPipelineStatuses={dealPipelineStatuses}
      dealStages={dealStages}
      darkModeLogo={darkModeLogo}
      lightModeLogo={lightModeLogo}
      noteStatuses={noteStatuses}
      taskTypes={taskTypes}
      title={title}
      googleWorkplaceDomain={googleWorkplaceDomain}
    >
      <ResponsiveAdmin
        dataProvider={dataProvider}
        authProvider={authProvider}
        i18nProvider={i18nProvider}
        store={localStorageStore(undefined, "CRM")}
        loginPage={StartPage}
        requireAuth
        disableTelemetry
        {...rest}
      />
    </ConfigurationProvider>
  );
};

const DesktopAdmin = (props: CoreAdminProps) => {
  return (
    <Admin layout={Layout} dashboard={BldrOsDashboard} {...props}>
      <CustomRoutes noLayout>
        <Route path={SignupPage.path} element={<SignupPage />} />
        <Route path={ConfirmationRequired.path} element={<ConfirmationRequired />} />
        <Route path={SetPasswordPage.path} element={<SetPasswordPage />} />
        <Route path={ForgotPasswordPage.path} element={<ForgotPasswordPage />} />
        <Route path={OAuthConsentPage.path} element={<OAuthConsentPage />} />

        {/* Public routes */}
        <Route path={PublicEstimatePage.path} element={<PublicEstimatePage />} />
        <Route path={CustomerPortalPage.path} element={<CustomerPortalPage />} />
        <Route path={VendorPortalPage.path} element={<VendorPortalPage />} />
      </CustomRoutes>

      <CustomRoutes>
        <Route path={SettingsPage.path} element={<SettingsPage />} />
        <Route path={OrgSettingsPage.path} element={<OrgSettingsPage />} />
        <Route path={SubList.path} element={<SubList />} />
        <Route path={SuperAdminDashboard.path} element={<SuperAdminDashboard />} />
        <Route path={PipelineDashboard.path} element={<PipelineDashboard />} />
        <Route path={VendorList.path} element={<VendorList />} />
        <Route path={ImportPage.path} element={<ImportPage />} />
      </CustomRoutes>

      {/* Core Atomic CRM resources */}
      <Resource name="deals" {...deals} />
      <Resource name="contacts" {...contacts} />
      <Resource name="companies" {...companies} />
      <Resource name="sales" {...sales} />

      {/* Notes / tasks / tags */}
      <Resource name="contact_notes" />
      <Resource name="deal_notes" />
      <Resource name="tasks" />
      <Resource name="tags" />

      {/* BldrOS data tables used by hooks / panels */}
      <Resource name="events" />
      <Resource name="calls" />
      <Resource name="sms_messages" />
      <Resource name="organizations" />
      <Resource name="org_members" />
      <Resource name="org_phone_numbers" />
      <Resource name="org_campaigns" />

      {/* ✅ These MUST spread the modules so pages/routes exist */}
      <Resource name="estimates" {...estimates} />
      <Resource name="jobs" {...jobs} />
      <Resource name="invoices" {...invoices} />

      {/* Other BldrOS tables (no menu pages yet, but data accessible) */}
      <Resource name="estimate_line_items" />
      <Resource name="job_expenses" />
      <Resource name="subcontractors" />
      <Resource name="job_assignments" />
      <Resource name="check_ins" />
      <Resource name="vendors" />
      <Resource name="purchase_orders" />
      <Resource name="daily_reports" />
      <Resource name="change_orders" />
      <Resource name="audit_log" />
      <Resource name="reviews" />
    </Admin>
  );
};

const MobileAdmin = (props: CoreAdminProps) => {
  const queryClient = new QueryClient({
    defaultOptions: { queries: { gcTime: 1000 * 60 * 60 * 24 } }, // 24 hours
  });

  const asyncStoragePersister = createAsyncStoragePersister({
    storage: localStorage,
  });

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{ persister: asyncStoragePersister }}
    >
      <Admin
        queryClient={queryClient}
        layout={MobileLayout}
        dashboard={MobileBldrOsDashboard}
        {...props}
      >
        <CustomRoutes noLayout>
          <Route path={SignupPage.path} element={<SignupPage />} />
          <Route path={ConfirmationRequired.path} element={<ConfirmationRequired />} />
          <Route path={SetPasswordPage.path} element={<SetPasswordPage />} />
          <Route path={ForgotPasswordPage.path} element={<ForgotPasswordPage />} />
          <Route path={OAuthConsentPage.path} element={<OAuthConsentPage />} />

          {/* Public routes */}
          <Route path={PublicEstimatePage.path} element={<PublicEstimatePage />} />
          <Route path={CustomerPortalPage.path} element={<CustomerPortalPage />} />
          <Route path={VendorPortalPage.path} element={<VendorPortalPage />} />
        </CustomRoutes>

        {/* Mobile-first resources */}
        <Resource
          name="contacts"
          list={ContactListMobile}
          show={ContactShow}
          recordRepresentation={contacts.recordRepresentation}
        />
        <Resource name="companies" show={CompanyShow} />
        <Resource name="tasks" list={MobileTasksList} />

        {/* Data tables needed by components */}
        <Resource name="events" />
        <Resource name="calls" />
        <Resource name="sms_messages" />
        <Resource name="organizations" />
        <Resource name="org_members" />
        <Resource name="org_phone_numbers" />
        <Resource name="org_campaigns" />

        {/* Allow mobile to navigate/use these too if UI links to them */}
        <Resource name="estimates" {...estimates} />
        <Resource name="jobs" {...jobs} />
        <Resource name="invoices" {...invoices} />
      </Admin>
    </PersistQueryClientProvider>
  );
};